// Equivalente de AppRoutes.tsx (packages/hce) escopeado solo a
// /hce/paciente/:id/* — el basename="/hce/paciente" ya está puesto en
// main.tsx (BrowserRouter), así que acá las rutas arrancan en ":id".
import type { JSX } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { LandingAcceso } from './auth/LandingAcceso';
import { UsuariosPage } from './admin/UsuariosPage';
import { AuditoriaPage } from './admin/AuditoriaPage';
import { ComitePage } from './admin/ComitePage';
import { EmergenciasPage } from './admin/EmergenciasPage';
import { AlertasVivasPage } from './admin/AlertasVivasPage';
import { DescargarHistoriasPage } from './salida/DescargarHistoriasPage';
import { AccesoAuditoriaPage } from './auditoria/AccesoAuditoriaPage';
import { ChecklistAuditoriaPage } from './auditoria/ChecklistAuditoriaPage';
import { DashboardPage } from './dashboard/DashboardPage';
import { MiCertificadoPage } from './dashboard/MiCertificadoPage';
import { TelemedicinaSplitView } from './dashboard/TelemedicinaSplitView';
import { AlergiasTab } from './paciente/AlergiasTab';
import { CobroTab } from './paciente/CobroTab';
import { ConsentimientosTab } from './paciente/ConsentimientosTab';
import { HistorialCambiosTab } from './paciente/HistorialCambiosTab';
import { DatosDemograficosTab } from './paciente/DatosDemograficosTab';
import { DocumentosTab } from './paciente/DocumentosTab';
import { FacesheetTab } from './paciente/FacesheetTab';
import { FarmaciaTab } from './paciente/FarmaciaTab';
import { GrupoFormularioTab } from './paciente/GrupoFormularioTab';
import { CODIGOS_CONSENTIMIENTO } from './lib/consentimientos';
import { FORMULARIOS_DISCRETOS } from './formulario/discretos';
import { Formulario001Page } from './formulario/Formulario001Page';
import { FormularioDiscretoPage } from './formulario/FormularioDiscretoPage';
import { FormularioConHistorico } from './formulario/FormularioConHistorico';
import { FormularioFemoPage } from './formulario/FormularioFemoPage';
import { MotorFormularioConsentimiento } from './formulario/MotorFormularioConsentimiento';
import { CODIGO_REVOCATORIA_024, RevocatoriaConsentimiento024 } from './formulario/RevocatoriaConsentimiento024';
import { MotorFormularioPage } from './formulario/MotorFormularioPage';
import { HistoriaTab } from './paciente/HistoriaTab';
import { LabsTab } from './paciente/LabsTab';
import { MedicamentosTab } from './paciente/MedicamentosTab';
import { NotasTab } from './paciente/NotasTab';
import { NotaTurnoTab } from './paciente/NotaTurnoTab';
import { PatientChartPage } from './paciente/PatientChartPage';
import { ProblemasTab } from './paciente/ProblemasTab';
import { SeguimientoPacienteTab } from './paciente/SeguimientoPacienteTab';
import { SignosVitalesTab } from './paciente/SignosVitalesTab';
import { PacientesPage } from './pacientes/PacientesPage';
import { HistoriasPage } from './historias/HistoriasPage';
import { AgendaPage } from './agenda/AgendaPage';
import { ReportesPage } from './reportes/ReportesPage';
import { SalaPage } from './sala/SalaPage';
import { FarmaciaApp } from './farmacia/FarmaciaApp';
import { ColaRecetas } from './farmacia/ColaRecetas';
import { CatalogoFarmacia } from './farmacia/CatalogoFarmacia';
import { InventarioFarmacia } from './farmacia/InventarioFarmacia';
import { HistorialFarmacia } from './farmacia/HistorialFarmacia';
import { RequiereNoFarmacia } from './farmacia/RequiereNoFarmacia';
import { OrdenesFarmacia } from './farmacia/OrdenesFarmacia';
import { ComprobantesFarmacia } from './farmacia/ComprobantesFarmacia';
import { ConveniosFarmacia } from './farmacia/ConveniosFarmacia';
import { CajaFarmacia } from './farmacia/CajaFarmacia';
import { ReportesFarmacia } from './farmacia/ReportesFarmacia';
import { LicenciaActaPage } from './licencia/LicenciaActaPage';
import { ActivarCuentaPage } from './auth/ActivarCuentaPage';
import { SeguridadCuentaPage } from './auth/SeguridadCuentaPage';
import { NuevaContrasenaPage } from './auth/NuevaContrasenaPage';
import { VerificarCorreoPage } from './auth/VerificarCorreoPage';

/**
 * El árbol de rutas de la ficha del paciente, factorizado en una función
 * (no un componente) para poder reusarlo tal cual en dos lugares: el nivel
 * raíz (`/:id/...`, uso normal) y anidado bajo `/telemedicina/:citaId/...`
 * (Módulo G — la mitad "ficha" de la pantalla dividida usa la MISMA
 * PatientChartPage completa vía <Outlet/>, no una versión resumida aparte
 * que habría que mantener sincronizada con esta lista).
 */
function rutasFichaPaciente(): JSX.Element {
  return (
    <Route path=":id" element={<PatientChartPage />}>
      <Route index element={<FacesheetTab />} />
      <Route path="resumen" element={<FacesheetTab />} />
      <Route path="historia" element={<HistoriaTab />} />
      <Route path="problemas" element={<ProblemasTab />} />
      <Route path="medicamentos" element={<MedicamentosTab />} />
      <Route path="alergias" element={<AlergiasTab />} />
      <Route path="signos-vitales" element={<SignosVitalesTab />} />
      {/* Enfermería (Fase 3): pestañas propias de la ficha — mismo
          FormularioDiscretoPage (gate por especialidad + PDF/firma) que
          "formulario/<código>", con un segmento de un solo nivel para que
          LinkTabs marque la pestaña activa. */}
      <Route path="kardex" element={<FormularioDiscretoPage codigo="022" />} />
      <Route path="intervenciones" element={<FormularioDiscretoPage codigo="120" />} />
      {/* Auditoría de visibilidad por especialidad (2026-09-27): 008
          (Emergencia) es un extra de Enfermería sin pestaña propia — mismo
          patrón que kardex/intervenciones (FormularioDiscretoPage suelto,
          SIN crear Encounter), para que el sidebar reducido de Enfermería
          (tabsEnfermeria, PatientChartPage.tsx) pueda enlazarlo. */}
      <Route path="emergencia" element={<FormularioDiscretoPage codigo="008" />} />
      {/* "Nota de turno" es la única de las cuatro que NO es el formulario
          pelado: envuelve al mismo FormularioDiscretoPage y le suma el
          historial de notas SOAPIE del paciente (antes el Composition se
          creaba y nadie lo leía). Ver NotaTurnoTab.tsx. */}
      <Route path="nota-turno" element={<NotaTurnoTab />} />
      <Route path="notas" element={<NotasTab />} />
      <Route path="cobro" element={<CobroTab />} />
      <Route path="seguimiento" element={<SeguimientoPacienteTab />} />
      <Route path="formulario/001" element={<Formulario001Page />} />
      {/* 024 + los 6 consentimientos nuevos (ver lib/consentimientos.ts) —
          una ruta estática por código, mismo patrón que FORMULARIOS_DISCRETOS
          de más abajo, para no romper los links ya existentes a
          "formulario/024" (SelectorFormularios.tsx) con un `:codigo`. */}
      {CODIGOS_CONSENTIMIENTO.map((codigo) => (
        <Route key={codigo} path={`formulario/${codigo}`} element={<MotorFormularioConsentimiento codigo={codigo} />} />
      ))}
      {/* Revocatoria del 024 (bloque E del papel): documento propio, se abre desde la pestaña Consentimientos con `?consent=<id>`. */}
      <Route path={`formulario/${CODIGO_REVOCATORIA_024}`} element={<RevocatoriaConsentimiento024 />} />
      {/* Grupo 5 (Módulo H) — FEMO es el único código de este grupo que
          necesita su propia página de ruta (wizard con firma manuscrita del
          trabajador entre medio, igual patrón que 024) — las 3 escalas son
          Questionnaire comunes, ya cubiertas por el catch-all
          "formulario/:codigo" de más abajo, sin necesitar código nuevo acá.
          054/055 YA NO son Questionnaire (Anexo 1 MSP, discretos del Grupo 4
          — ver discretos/formularioResumenMsp.tsx): les toca la ruta
          estática de más abajo, cubiertos automáticamente por
          FORMULARIOS_DISCRETOS. */}
      <Route path="formulario/FEMO" element={<FormularioFemoPage />} />
      {/* Grupo 4 (Módulo H) — códigos discretos, ruta estática por código
          (rankea por encima de "formulario/:codigo" en React Router v6 sin
          importar el orden de declaración) para no necesitar un dispatcher
          aparte. Fuente única de la lista: FORMULARIOS_DISCRETOS. El código
          se pasa como prop (no hay `:codigo` en el path, es estático) — ver
          el comentario de FormularioDiscretoPageProps. */}
      {Object.keys(FORMULARIOS_DISCRETOS).map((codigo) => (
        <Route
          key={codigo}
          path={`formulario/${codigo}`}
          element={CODIGOS_CON_HISTORICO_QUESTIONNAIRE.has(codigo) ? <FormularioConHistorico codigo={codigo} /> : <FormularioDiscretoPage codigo={codigo} />}
        />
      ))}
      <Route path="formulario/:codigo" element={<MotorFormularioPage />} />
      <Route path="referencias" element={<GrupoFormularioTab grupo="referencias" />} />
      <Route path="mediciones" element={<GrupoFormularioTab grupo="mediciones" />} />
      <Route path="quirurgico" element={<GrupoFormularioTab grupo="quirurgico" />} />
      <Route path="pediatria" element={<GrupoFormularioTab grupo="pediatria" />} />
      <Route path="gineco-obstetricia" element={<GrupoFormularioTab grupo="gineco-obstetricia" />} />
      <Route path="adolescentes" element={<GrupoFormularioTab grupo="adolescentes" />} />
      <Route path="adulto-mayor" element={<GrupoFormularioTab grupo="adulto-mayor" />} />
      <Route path="odontologia" element={<GrupoFormularioTab grupo="odontologia" />} />
      <Route path="psicologia" element={<GrupoFormularioTab grupo="psicologia" />} />
      <Route path="uci" element={<GrupoFormularioTab grupo="uci" />} />
      <Route path="seguridad-paciente" element={<GrupoFormularioTab grupo="seguridad-paciente" />} />
      <Route path="escalas-clinicas" element={<GrupoFormularioTab grupo="escalas-clinicas" />} />
      <Route path="farmacia" element={<FarmaciaTab />} />
      <Route path="laboratorio" element={<LabsTab />} />
      <Route path="documentos" element={<DocumentosTab />} />
      <Route path="consentimientos" element={<ConsentimientosTab />} />
      <Route path="historial-cambios" element={<HistorialCambiosTab />} />
      <Route path="datos-demograficos" element={<DatosDemograficosTab />} />
    </Route>
  );
}

/** Códigos que antes eran Questionnaire y conservan respuestas históricas reabribles con `?respuestaId=`. */
const CODIGOS_CON_HISTORICO_QUESTIONNAIRE = new Set(['002', '003', '017', '018', '056', '056A', '056B']);

export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      {/* Bug real en vivo 2026-09-02: el basename ("/hce/paciente") no tenía
          ninguna ruta índice — entrar exacto a esa raíz (p. ej. tras el
          redirect nuevo de main.tsx desde el dominio pelado) no matcheaba
          nada y quedaba en blanco, con sesión válida y todo. */}
      <Route index element={<Navigate to="dashboard" replace />} />
      {/* Rediseño mayor 2026-08-31 (Módulos C/D/E/G) — cableado temporal
          para verificación en dev bajo el basename compartido. Cuando se
          corte a modo subdominio, estas rutas se hoistean a la raíz real de
          la app y dejan de convivir con /hce/paciente/*. */}
      <Route path="login" element={<LandingAcceso />} />
      <Route path="usuarios" element={<UsuariosPage />} />
      {/* Modo Auditor (2026-09-09) — "auditoria" es el panel del admin
          (sesión de hce-auth.ts); "auditoria/acceso" y "auditoria/checklist"
          son públicas, sin sesión de Medplum NI de admin (token efímero de
          hce-auth.ts, otro `kind`) — las tres van en RUTAS_SIN_MEDPLUM de
          main.tsx. */}
      <Route path="auditoria" element={<AuditoriaPage />} />
      <Route path="comite" element={<ComitePage />} />
      {/* MT-042 (break-glass): revisión de los accesos de emergencia al expediente por el Responsable de privacidad (el administrador). */}
      <Route path="emergencias" element={<EmergenciasPage />} />
      {/* Alertas de completitud en vivo (2026-09-10) — widget de admin
          agregado por consultorio, ver AlertasVivasPage.tsx. */}
      <Route path="alertas" element={<AlertasVivasPage />} />
      {/* Salida del consultorio: «Descargar mis historias», única pantalla del administrador durante el período de descarga tras la baja. */}
      <Route path="descargar-historias" element={<DescargarHistoriasPage />} />
      {/* Autoservicio de firma electrónica (2026-09-14) — sin
          RequiereNoFarmacia a propósito: la usan personal-medico Y
          director-tecnico-farmacia, ver MiCertificadoPage.tsx. */}
      <Route path="mi-certificado" element={<MiCertificadoPage />} />
      {/* Seguridad de la cuenta (MT-043-C): verificación en dos pasos del personal clínico y del Director Técnico; mismo criterio que mi-certificado. */}
      <Route path="seguridad" element={<SeguridadCuentaPage />} />
      <Route path="auditoria/acceso" element={<AccesoAuditoriaPage />} />
      <Route path="auditoria/checklist" element={<ChecklistAuditoriaPage />} />
      <Route
        path="dashboard"
        element={
          <RequiereNoFarmacia>
            <DashboardPage />
          </RequiereNoFarmacia>
        }
      />
      {/* Sala del HCE (Sala CRM↔HCE, rebanada 5): los pacientes del día del
          médico por hora de llegada. Ruta estática, mismo criterio que las
          de abajo; el director técnico de farmacia no tiene Sala. */}
      <Route
        path="sala"
        element={
          <RequiereNoFarmacia>
            <SalaPage />
          </RequiereNoFarmacia>
        }
      />
      {/* Rediseño mayor 2026-09-02 (Fase 1, "destinos reales del nav") —
          ruta estática, rankea por encima de ":id" en React Router v6 sin
          importar el orden de declaración. */}
      <Route
        path="pacientes"
        element={
          <RequiereNoFarmacia>
            <PacientesPage />
          </RequiereNoFarmacia>
        }
      />
      <Route
        path="historias"
        element={
          <RequiereNoFarmacia>
            <HistoriasPage />
          </RequiereNoFarmacia>
        }
      />
      <Route
        path="agenda"
        element={
          <RequiereNoFarmacia>
            <AgendaPage />
          </RequiereNoFarmacia>
        }
      />
      <Route
        path="reportes"
        element={
          <RequiereNoFarmacia>
            <ReportesPage />
          </RequiereNoFarmacia>
        }
      />
      {/* App de Farmacia Interna (2026-09-11) — pantalla de inicio del
          Director Técnico Q.F./B.F., top-level (no anidada en :id/...): un
          farmacéutico trabaja por cola de recetas de todo el consultorio, no
          paciente por paciente. Ver farmacia/FarmaciaApp.tsx. */}
      <Route path="farmacia" element={<FarmaciaApp />}>
        <Route index element={<ColaRecetas />} />
        <Route path="catalogo" element={<CatalogoFarmacia />} />
        <Route path="inventario" element={<InventarioFarmacia />} />
        <Route path="historial" element={<HistorialFarmacia />} />
        {/* Facturación (Fase 3-4, 2026-09-11): Órdenes/Comprobantes/Convenios/
            Caja/Reportes propios, agrupados bajo un sub-nav — ver FarmaciaApp.tsx. */}
        <Route path="facturacion" element={<OrdenesFarmacia />} />
        <Route path="facturacion/comprobantes" element={<ComprobantesFarmacia />} />
        <Route path="facturacion/convenios" element={<ConveniosFarmacia />} />
        <Route path="facturacion/caja" element={<CajaFarmacia />} />
        <Route path="facturacion/reportes" element={<ReportesFarmacia />} />
      </Route>
      {/* Retiro del legado packages/hce (2026-09-13): lo que antes solo
          servía la app vieja. "setpassword"/"verifyemail" son los enlaces que
          arma Medplum con su appBaseUrl (públicas, sin sesión — ver
          PREFIJOS_SIN_MEDPLUM en main.tsx); "licencia/acta" es el Acta de
          Entrega-Recepción del Modo Consulta (con sesión de Medplum). */}
      {/* Enlace de activación de cuenta (2026-09-13): lo manda la bóveda por
          correo cuando el admin crea la cuenta — pública, sin sesión. */}
      <Route path="activar/:token" element={<ActivarCuentaPage />} />
      <Route path="setpassword/:id/:secret" element={<NuevaContrasenaPage />} />
      <Route path="verifyemail/:id/:secret" element={<VerificarCorreoPage />} />
      <Route path="licencia/acta" element={<LicenciaActaPage />} />
      <Route path="telemedicina/:citaId" element={<TelemedicinaSplitView />}>
        {rutasFichaPaciente()}
      </Route>
      {rutasFichaPaciente()}
    </Routes>
  );
}
